import React, { useEffect, useMemo, useState } from "react";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import {
  BarcodeDetailIcon,
  LeafDetailIcon,
  PalletDetailIcon,
} from "../Icons/ProductDetailIcons";
import { Alert, Box, Button, Card, Snackbar, Typography } from "@mui/material";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addToCart } from "../../store";
import { productToCartItem } from "../utils/productToCartItem";

const PRODUCT_CARD_RED = "#e31837";

const addToQuoteButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  bgcolor: PRODUCT_CARD_RED,
  boxShadow: "0 4px 12px rgba(227, 24, 55, 0.28)",
  "&:hover": {
    bgcolor: "#c8102e",
    boxShadow: "0 6px 16px rgba(227, 24, 55, 0.34)",
  },
};

const pageShell = (children) => (
  <Box
    sx={{
      pt: "clamp(96px, 12vh, 136px)",
      pb: { xs: 4, md: 6 },
      px: { xs: 1.5, md: 2.5 },
      maxWidth: 1300,
      mx: "auto",
    }}
  >
    {children}
  </Box>
);

const PRODUCT_DETAIL_BLUE = "#0b3f7b";

const formatVatPercent = (vat) => {
  if (vat == null || vat === "") return "-";
  const text = String(vat).trim();
  if (!text) return "-";
  const n = Number(text.replace(/%/g, "").trim());
  if (!Number.isFinite(n)) return text.includes("%") ? text : `${text}%`;
  return `${n}%`;
};

const getProductDetailRows = (product) => [
  {
    icon: LocalOfferOutlinedIcon,
    label: "Product Code:",
    value: product?.sku || product?.code || "-",
  },
  {
    icon: Inventory2OutlinedIcon,
    label: "Case Size:",
    value: product?.case_size || "-",
  },
  {
    icon: LayersOutlinedIcon,
    label: "Layer:",
    value: product?.layer || "-",
  },
  {
    icon: PalletDetailIcon,
    label: "Pallet:",
    value: product?.pallet || "-",
  },
  {
    icon: BarcodeDetailIcon,
    label: "Unit Barcode:",
    value: product?.unit_barcode || "-",
  },
  {
    icon: BarcodeDetailIcon,
    label: "Case Barcode:",
    value: product?.case_barcode || "-",
  },
  {
    icon: PercentOutlinedIcon,
    label: "VAT:",
    value: formatVatPercent(product?.vat),
  },
];

const getProductImage = (product) => {
  if (product?.image_url) return product.image_url;
  if (typeof product?.image === "string" && product.image.startsWith("http")) {
    return product.image;
  }
  if (typeof product?.image === "string" && product.image) {
    return `https://api.uktomars.co.uk/storage/${product.image}`;
  }
  return "";
};

const ProductDetailsPage = () => {
  const { id } = useParams();
  return <ProductDetailsContent key={String(id || "")} />;
};

const getListingReturnPath = (locationState) => {
  const fromState = locationState?.from;
  if (typeof fromState === "string" && fromState.startsWith("/")) {
    return fromState;
  }
  try {
    const stored = sessionStorage.getItem("uktm:products-list-return");
    if (typeof stored === "string" && stored.startsWith("/")) return stored;
  } catch {
    // ignore storage errors
  }
  return "/products";
};

const ProductDetailsContent = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const listingReturnPath = getListingReturnPath(location.state);
  const [product, setProduct] = useState(location.state?.product || null);
  const [loading, setLoading] = useState(!location.state?.product);
  const [error, setError] = useState("");
  const [cartNotice, setCartNotice] = useState({ open: false, message: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;
    if (!id) return undefined;

    const initial = location.state?.product;
    const hasPlaceholder = initial && String(initial.id) === String(id);
    if (hasPlaceholder) {
      setProduct(initial);
    }

    const loadProduct = async () => {
      try {
        if (!hasPlaceholder) setLoading(true);
        const response = await fetch(
          `https://api.uktomars.co.uk/api/products/${encodeURIComponent(id)}`,
        );
        if (!response.ok) throw new Error("Failed to load product");
        const payload = await response.json();
        const data = payload?.data;
        const resolved =
          data && typeof data === "object" && !Array.isArray(data)
            ? data
            : null;

        if (isMounted) {
          if (resolved?.id != null) {
            setProduct(resolved);
            setError("");
          } else if (!hasPlaceholder) {
            setError("Product not found.");
          }
        }
      } catch {
        if (isMounted && !hasPlaceholder) {
          setError("Unable to load product right now.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProduct();
    return () => {
      isMounted = false;
    };
    // Intentionally keyed by route id; location.state is read per run for optional placeholder.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when product id changes
  }, [id]);

  const ingredientsText = (
    product?.ingredients ||
    product?.description ||
    ""
  ).trim();

  const relatedProducts = useMemo(() => {
    const list = Array.isArray(product?.related_products)
      ? product.related_products
      : [];
    return list
      .filter(
        (entry) =>
          entry && entry.id != null && String(entry.id) !== String(product?.id),
      )
      .slice(0, 8);
  }, [product]);

  return pageShell(
    <>
      <Box>
        <Typography sx={{ fontSize: 14, color: "#334155", mb: 1.5 }}>
          <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>
            Home
          </Link>{" "}
          <Box component="span" sx={{ color: "#94a3b8", mx: 0.6 }}>
            »
          </Box>
          <Link
            to={listingReturnPath}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Shop
          </Link>{" "}
          <Box component="span" sx={{ color: "#94a3b8", mx: 0.6 }}>
            »
          </Box>
          <Box component="span" sx={{ color: "#0f172a", fontWeight: 600 }}>
            {product?.name || "Product"}
          </Box>
        </Typography>

        {loading ? (
          <Typography>Loading product...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Card
            elevation={0}
            sx={{
              maxWidth: 960,
              width: "100%",
              mx: "auto",
              border: "1px solid #dbe3ee",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#ffffff",
              boxShadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 0.95fr) minmax(0, 1.05fr)",
              },
            }}
          >
            <Box
              sx={{
                minHeight: { xs: 280, md: 520 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#ffffff",
                p: { xs: 2, md: 3 },
                borderRight: { xs: "none", md: "1px solid #e8edf3" },
                borderBottom: { xs: "1px solid #e8edf3", md: "none" },
              }}
            >
              {getProductImage(product) ? (
                <img
                  src={getProductImage(product)}
                  alt={product?.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Typography sx={{ color: "#98a1af", fontSize: 14 }}>
                  No image
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                p: { xs: 2, md: 3 },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 20, md: 26 },
                  fontWeight: 700,
                  color: PRODUCT_DETAIL_BLUE,
                  lineHeight: 1.3,
                  mb: { xs: 1.5, md: 2 },
                }}
              >
                {product?.name || "Product"}
              </Typography>

              <Box sx={{ mb: 2 }}>
                {getProductDetailRows(product).map((row, index, rows) => {
                  const RowIcon = row.icon;
                  return (
                    <Box
                      key={row.label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        py: { xs: 0.9, md: 1 },
                        borderBottom:
                          index < rows.length - 1
                            ? "1px solid #e8edf3"
                            : "none",
                      }}
                    >
                      <RowIcon
                        sx={{
                          fontSize: 20,
                          color: PRODUCT_DETAIL_BLUE,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: { xs: 13, md: 14.5 },
                          fontWeight: 700,
                          color: PRODUCT_DETAIL_BLUE,
                          flexShrink: 0,
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: 13, md: 14.5 },
                          fontWeight: 500,
                          color: "#1e293b",
                          ml: "auto",
                          textAlign: "right",
                          wordBreak: "break-word",
                        }}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Button
                variant="contained"
                fullWidth
                startIcon={
                  <AddShoppingCartOutlinedIcon sx={{ fontSize: 22 }} />
                }
                onClick={() => {
                  if (!product?.id) return;
                  dispatch(
                    addToCart(productToCartItem(product, getProductImage)),
                  );
                  const label = (product?.name ?? "Item").trim() || "Item";
                  setCartNotice({
                    open: true,
                    message: `${label} added to cart`,
                  });
                }}
                sx={{
                  ...addToQuoteButtonSx,
                  fontSize: { xs: 15, md: 16 },
                  borderRadius: 1.5,
                  py: 1.25,
                  letterSpacing: 0.2,
                }}
              >
                Add to Quote
              </Button>

              {ingredientsText ? (
                <Box sx={{ mt: { xs: 2, md: 2.5 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 0.75,
                    }}
                  >
                    <LeafDetailIcon
                      sx={{ fontSize: 20, color: PRODUCT_DETAIL_BLUE }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: 14, md: 15 },
                        fontWeight: 700,
                        color: PRODUCT_DETAIL_BLUE,
                      }}
                    >
                      Ingredients:
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 12.5, md: 13.5 },
                      color: "#475569",
                      lineHeight: 1.65,
                    }}
                  >
                    {ingredientsText}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Card>
        )}

        {!loading && !error && relatedProducts.length > 0 ? (
          <Box
            sx={{
              mt: { xs: 4, md: 5 },
              pt: { xs: 3, md: 4 },
              borderTop: "1px solid #e8ecf2",
            }}
          >
            <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: 22, md: 28 },
                  fontWeight: 800,
                  color: "#0f172a",
                  letterSpacing: "0.02em",
                  wordSpacing: "0.12em",
                  lineHeight: 1.2,
                }}
              >
                Related products
              </Typography>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
                gap: { xs: 2, md: 2.25 },
              }}
            >
              {relatedProducts.map((rel) => {
                const img = getProductImage(rel);
                return (
                  <Card
                    key={rel.id}
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      overflow: "hidden",
                      border: "1px solid #e8ecf2",
                      bgcolor: "#fff",
                      boxShadow:
                        "0 1px 0 rgba(15, 23, 42, 0.04), 0 12px 40px -24px rgba(15, 23, 42, 0.12)",
                      transition:
                        "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          "0 1px 0 rgba(15, 23, 42, 0.06), 0 20px 48px -20px rgba(15, 23, 42, 0.18)",
                        borderColor: "#dce3ec",
                        "& .related-card-title": { color: "#0f2a7a" },
                      },
                    }}
                  >
                    <Box
                      component={Link}
                      to={`/products/${rel.id}`}
                      state={{ product: rel }}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      <Box
                        sx={{
                          aspectRatio: "1 / 1",
                          background:
                            "radial-gradient(ellipse at 50% 30%, #f1f5f9 0%, #f8fafc 55%, #eef2f7 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 1.5,
                          borderBottom: "1px solid #eef2f7",
                        }}
                      >
                        {img ? (
                          <img
                            src={img}
                            alt={rel?.name || ""}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              filter:
                                "drop-shadow(0 8px 16px rgba(15, 23, 42, 0.08))",
                            }}
                          />
                        ) : (
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#94a3b8",
                              fontWeight: 500,
                            }}
                          >
                            No image
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ px: 1.5, pt: 1.5, pb: 1, flex: 1 }}>
                        <Typography
                          className="related-card-title"
                          sx={{
                            fontWeight: 700,
                            fontSize: 15,
                            color: "#0f172a",
                            lineHeight: 1.35,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            transition: "color 0.2s ease",
                          }}
                        >
                          {rel?.name || "Product"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                      <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        startIcon={
                          <AddShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />
                        }
                        onClick={() => {
                          if (!rel?.id) return;
                          dispatch(
                            addToCart(productToCartItem(rel, getProductImage)),
                          );
                          const label = (rel?.name ?? "Item").trim() || "Item";
                          setCartNotice({
                            open: true,
                            message: `${label} added to cart`,
                          });
                        }}
                        sx={{
                          ...addToQuoteButtonSx,
                          fontSize: 13,
                          letterSpacing: 0.02,
                          py: 0.85,
                          borderRadius: 1.5,
                        }}
                      >
                        Add to Quote
                      </Button>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          </Box>
        ) : null}
      </Box>

      <Snackbar
        open={cartNotice.open}
        autoHideDuration={4000}
        onClose={() => setCartNotice((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCartNotice((n) => ({ ...n, open: false }))}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            maxWidth: 480,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.15)",
          }}
        >
          {cartNotice.message}
        </Alert>
      </Snackbar>
    </>,
  );
};

export default ProductDetailsPage;
